<script>
import useSharedData from "@/use/SharedData";
import {computed, ref, watch} from "vue";
import WMenuValidationAction from "@/components/WMenuValidationAction.vue";
import useSharedMethod from "@/use/SharedMethod";
import {useSecurityStore} from "@/stores/Security";

export default {
    name: "WDataTable",
    components: {WMenuValidationAction},
    props: {
        value: Array,
        items: Array,
        headers: Array,
        search: String,
        identity: String,
        actions: Array,
        loading: Boolean,
        total: Number,
        showSelect: Boolean,
        singleSelect: Boolean,
        showDefaultItemColor: Boolean,
        showExpand: Boolean,
        showAllExpanded: Boolean,
        showFirstExpanded: Boolean,
        singleExpand: Boolean,
        expandIcon: String,
        sortBy: String,
        descending: Boolean,
        externalItemsPerPage: Number,
        disablePagination: Boolean,
        fixedHeader: Boolean,
        height: String,
        showValidation: Boolean,
        rootPath: String,
        module: String,
        groupBy: String,
        showGroupBy: Boolean,
        itemKey: String,
        itemClass: [String, Function],
    },
    emits: ["rowDbClick", "itemClick"],
    setup(props, {emit}) {
        const securityStore = useSecurityStore();
        const {
            page,
            itemsPerPage,
            pageCount,
            paginationListLength,
            selectedOne,
            itemColor
        } = useSharedData();


        if (props.externalItemsPerPage)
            itemsPerPage.value = props.externalItemsPerPage;

        const transformedHeaders = computed(() => {
            return props.headers.map(header => ({
                ...header,
                class: 'text-no-wrap' // Adds the class to every header object
            }));
        });

        const {formatedDate} = useSharedMethod();

        const expanded = ref([]);
        const selected = ref([]);

        function rowDbClick(event, {item}) {
            emit("rowDbClick", item);
        }

        function rowColor(item) {
            const identityKey = props.identity || 'id';
            if (item[identityKey] === selectedOne.value[identityKey]) {
                return itemColor.value;
            }


            if (item.color)
                return item.color;

            if (item.validateOption || item.takeInChargeOption)
                return securityStore.userContext.validationColor;

            if (props.showDefaultItemColor)
                return "brown lighten-5";
        }

        function itemExpanded({item}) {
            itemClick(item);
        }

        function itemClick(item) {
            selectedOne.value = item;
            emit('itemClick', item);
        }

        watch(() => props.items, () => {
            if (props.showAllExpanded) {
                expanded.value = props.items;
            } else if (props.showFirstExpanded && props.items.length > 0) {
                expanded.value = [props.items[0]];
            }
        }, {deep: true, immediate: true});

        watch(() => props.value, () => {
            selected.value = props.value;
        }, {immediate: true, deep: true});

        watch(() => selected.value, () => {
            emit('input', selected.value);
        }, {deep: true});

        return {
            page,
            itemsPerPage,
            pageCount,
            paginationListLength,
            expanded,
            selected,
            transformedHeaders,
            rowColor,
            itemClick,
            itemExpanded,
            rowDbClick,
            formatedDate,
        }
    }
}
</script>

<template>
    <div>
        <v-skeleton-loader
            v-show="loading"
            ref="skeleton"
            :type="'table'"
            class="mx-auto"
        ></v-skeleton-loader>
        <v-data-table
            v-show="!loading"
            v-model="selected"
            :headers="transformedHeaders"
            :search="search"
            :items="items"
            :item-key="itemKey"
            :loading="loading"
            :sort-by="sortBy"
            :sort-desc="descending"
            dense
            :page.sync="page"
            :items-per-page="itemsPerPage"
            @page-count="pageCount = $event"
            hide-default-footer
            :disable-pagination="disablePagination"
            :fixed-header="fixedHeader"
            :show-select="showSelect"
            :single-select="singleSelect"
            :height="height"
            :item-class="itemClass || rowColor"
            @click:row="itemClick"
            @dblclick:row="rowDbClick"
            @item-expanded="itemExpanded"
            :show-expand="showExpand"
            :single-expand="singleExpand"
            :expand-icon="expandIcon"
            :expanded.sync="expanded"
            :show-group-by="showGroupBy"
            :group-by="groupBy"
        >

            <template v-slot:group.header="{ group, groupBy, isOpen, items,  toggle }">
                <slot
                    name="group-header"
                    :group="group"
                    :groupBy="groupBy"
                    :isOpen="isOpen"
                    :items="items"
                    :toggle="toggle"
                >
                </slot>
            </template>

            <!-- Dynamic header slots -->
            <template v-for="header in headers" v-slot:[`item.${header.value}`]="{ item }">
                <slot
                    :name="`item.${header.value}`"
                    :item="item"
                >
                    {{ item[header.value] }}
                </slot>
            </template>

            <template
                v-if="actions"
                v-slot:[`item.action`]="{ item }"
            >
                <v-menu bottom right>
                    <template v-slot:activator="{ on }">
                        <v-btn
                            dark
                            icon
                            color="blue darken-2"
                            v-on="on"
                            @click="itemClick(item)"
                        >
                            <v-icon>mdi-dots-horizontal</v-icon>
                        </v-btn>
                    </template>

                    <v-list nav dense>
                        <w-menu-validation-action
                            v-if="showValidation"
                            :item="item"
                            :rootPath="rootPath"
                            :module="module"
                        />
                        <template v-for="(action, index) in actions">
                            <v-list-item
                                :key="`action-${index}`"
                                @click="action.func(item)"
                                v-if="action.access(item)"
                                :disabled="action.disabled(item)"
                            >
                                <v-list-item-icon>
                                    <v-icon :color="action.color">{{ action.icon }}</v-icon>
                                </v-list-item-icon>
                                <v-list-item-title>
                                    {{ action.title }}
                                </v-list-item-title>
                            </v-list-item>
                        </template>
                    </v-list>
                </v-menu>
            </template>

            <template v-slot:[`item.validationStatus.lastState`]="{ item }">
                <w-chip
                    outlined
                    :text="item.validationStatus.autoValidate ? 'Validation Automatique' : item.validationStatus.lastState"
                />
            </template>
            <template v-slot:[`item.validationStatus.lastValidationDate`]="{ item }">
                <w-chip
                    outlined
                    :text="item.validationStatus.lastValidationDate ? formatedDate(item.validationStatus.lastValidationDate) : formatedDate(item.postDateTime)"
                />
            </template>
            <template v-slot:[`item.validationStatus.nextValidator`]="{ item }">
                <w-chip
                    v-if="item.validationStatus.nextValidator"
                    outlined
                    :text="item.validationStatus.nextValidatorFullName"
                />
            </template>

            <template v-slot:expanded-item="{ headers, item }">
                <slot
                    name="expanded-item"
                    :item="item"
                    :headers="headers"
                >
                </slot>
            </template>

            <template v-slot:footer="{}" v-if="!disablePagination">
                <br/>
                <div class="text-center">
                    <v-row justify="center">
                        <v-col md="6">
                            <v-pagination
                                v-model="page"
                                :length="pageCount"
                            ></v-pagination>
                        </v-col>
                        <v-col md="2">
                            <w-enum-select
                                v-model="itemsPerPage"
                                :items="paginationListLength"
                                :label="$t('common.items_per_page')"
                            />
                        </v-col>
                    </v-row>
                </div>
            </template>
        </v-data-table>

        <input type="hidden"/>
    </div>
</template>